import { useEffect, useState } from 'react';
import { apiJson } from '../api';
import '../pages/OnboardingPage.css';
import './HomeOnboardingBlock.css';

type Explicit = {
  worldviewBackground?: string;
  familiarityWithIslam?: string;
  motive?: string;
  reasoningPreference?: string;
  difficultyComfort?: string;
  explorationStyle?: 'open' | 'guided';
  preferredModuleDepth?: 'simple' | 'full' | 'study';
  adaptRecommendations?: boolean;
};

export function HomeOnboardingBlock({ onFinished }: { onFinished: () => void }) {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const [data, setData] = useState<Explicit>({
    explorationStyle: 'open',
    preferredModuleDepth: 'full',
    adaptRecommendations: true,
  });

  useEffect(() => {
    let raf = 0;
    raf = window.requestAnimationFrame(() => setMounted(true));
    return () => window.cancelAnimationFrame(raf);
  }, []);

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      await apiJson('/api/onboarding', {
        method: 'POST',
        body: JSON.stringify({ explicit: data }),
      });
      localStorage.setItem('wi_onboarding_done', '1');
      try {
        localStorage.setItem('wi_profile_explicit_v1', JSON.stringify(data));
      } catch {
        /* ignore */
      }
      onFinished();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  const skip = () => {
    try {
      localStorage.setItem('wi_onboarding_done', 'skipped');
    } catch {
      /* ignore */
    }
    onFinished();
  };

  return (
    <section
      className={`wi-adaptive-band wi-adaptive-band--premium wi-home-onboarding-block${mounted ? ' is-in-view' : ''}`}
      aria-label="Set up your profile"
    >
      <div className="wi-home-onboarding-card">
        <div className="wi-onboard">
          <div className="wi-onboard-inner">
            <p className="wi-onboard-kicker">Personalize</p>
            <h1>How would you like to begin?</h1>
            <p className="wi-onboard-lede">
              A few choices shape recommendations, ordering, and depth. You can change this anytime in Study.
            </p>

            {step === 0 ? (
              <section className="wi-onboard-panel">
                <label className="wi-field">
                  <span>Background</span>
                  <select
                    value={data.worldviewBackground ?? ''}
                    onChange={(e) => setData({ ...data, worldviewBackground: e.target.value || undefined })}
                  >
                    <option value="">Prefer not to say</option>
                    <option value="muslim">Muslim</option>
                    <option value="christian">Christian</option>
                    <option value="atheist">Atheist / secular</option>
                    <option value="agnostic">Agnostic</option>
                    <option value="jewish">Jewish</option>
                    <option value="hindu">Hindu</option>
                    <option value="spiritual_not_religious">Spiritual, not religious</option>
                    <option value="other">Other</option>
                  </select>
                </label>

                <label className="wi-field">
                  <span>Familiarity with Islam</span>
                  <select
                    value={data.familiarityWithIslam ?? ''}
                    onChange={(e) => setData({ ...data, familiarityWithIslam: e.target.value || undefined })}
                  >
                    <option value="">Prefer not to say</option>
                    <option value="totally_new">Totally new</option>
                    <option value="know_basics">Know basics</option>
                    <option value="studied_little">Studied a little</option>
                    <option value="researching_deeply">Researching deeply</option>
                  </select>
                </label>
              </section>
            ) : null}

            {step === 1 ? (
              <section className="wi-onboard-panel">
                <label className="wi-field">
                  <span>What matters most right now?</span>
                  <select value={data.motive ?? ''} onChange={(e) => setData({ ...data, motive: e.target.value || undefined })}>
                    <option value="">Prefer not to say</option>
                    <option value="curiosity">Curiosity</option>
                    <option value="comparison">Comparing beliefs</option>
                    <option value="concern">Concern or tension</option>
                    <option value="doubt">Doubt</option>
                    <option value="spiritual_search">Spiritual search</option>
                    <option value="specific_issue">A specific issue</option>
                  </select>
                </label>

                <label className="wi-field">
                  <span>Comfort with depth</span>
                  <select
                    value={data.difficultyComfort ?? ''}
                    onChange={(e) => setData({ ...data, difficultyComfort: e.target.value || undefined })}
                  >
                    <option value="overview_first">Overview first</option>
                    <option value="balanced_depth">Balanced depth</option>
                    <option value="direct_deep_dive">Direct, deeper dives</option>
                  </select>
                </label>
              </section>
            ) : null}

            {step === 2 ? (
              <section className="wi-onboard-panel">
                <label className="wi-field">
                  <span>Exploration style</span>
                  <select
                    value={data.explorationStyle ?? 'open'}
                    onChange={(e) => setData({ ...data, explorationStyle: e.target.value as 'open' | 'guided' })}
                  >
                    <option value="open">Open exploration</option>
                    <option value="guided">Guided paths first</option>
                  </select>
                </label>

                <label className="wi-field">
                  <span>Default reading depth on modules</span>
                  <select
                    value={data.preferredModuleDepth ?? 'full'}
                    onChange={(e) => setData({ ...data, preferredModuleDepth: e.target.value as 'simple' | 'full' | 'study' })}
                  >
                    <option value="simple">Short answer</option>
                    <option value="full">Full explanation</option>
                    <option value="study">Deeper analysis</option>
                  </select>
                </label>

                <label className="wi-field wi-field--check">
                  <input
                    type="checkbox"
                    checked={data.adaptRecommendations !== false}
                    onChange={(e) => setData({ ...data, adaptRecommendations: e.target.checked })}
                  />
                  <span>Let the platform adapt recommendations from what I explore</span>
                </label>
              </section>
            ) : null}

            {err ? <p className="wi-onboard-err">{err}</p> : null}

            <div className="wi-onboard-actions">
              {step > 0 ? (
                <button type="button" className="button ghost" onClick={() => setStep((s) => s - 1)}>
                  Back
                </button>
              ) : (
                <button type="button" className="button ghost" onClick={skip}>
                  Skip for now
                </button>
              )}

              {step < 2 ? (
                <button type="button" className="button primary" onClick={() => setStep((s) => s + 1)}>
                  Continue
                </button>
              ) : (
                <button type="button" className="button primary" disabled={busy} onClick={submit}>
                  {busy ? 'Saving…' : 'Save & continue'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

